import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import type { GroupNode as GroupNodeType } from '../../../../shared/ui/tree/tree.types';

import { Case, Group, Task, TaskDTO } from '@/models';
import {
  CaseService,
  CaseTaskWorkgroupType,
  CaseUtilsService,
  CommonService,
  ErrorService,
  GroupService,
  GroupUtilsService,
  LanguageService,
  NotificationService,
} from '@/services';
import { SpinnerButtonConfig } from '@/ui/spinner-button/spinner-button-config';

import { UuidService } from 'src/app/services/uuid/uuid.service';

type CaseWorkGroupsGroupNode = { id: string; type: CaseTaskWorkgroupType };

@Component({
    selector: 'app-case-workgroups',
    templateUrl: './case-workgroups.component.html',
    styleUrls: ['./case-workgroups.component.scss'],
    standalone: false
})
export class CaseWorkgroupsComponent implements OnInit {
  case: Case;
  treeData: GroupNodeType[] = [];
  selectedItems: CaseWorkGroupsGroupNode[] = [];
  allGroups: Group[] = [];
  loading: boolean;
  tasksPresent: boolean;

  new: boolean = false;

  saving: boolean = false;
  btnConfig: SpinnerButtonConfig;

  constructor(
    private caseService: CaseService,
    private router: Router,
    private errorService: ErrorService,
    private groupService: GroupService,
    private groupUtilsService: GroupUtilsService,
    private uuidService: UuidService,
    private notificationService: NotificationService,
    private commonService: CommonService,
    private caseUtilsService: CaseUtilsService,
    private languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.btnConfig = {
      text: this.languageService.translateWithCap('general.actions.save'),
      loading: this.saving,
    };
    var id = this.router.url.split('/')[2];
    if (id == 'nieuw') {
      this.new = true;
    } else {
      this.getCaseById(id);
    }

    this.groupService.getGroups(true).subscribe(groups => {
      this.allGroups = groups;
      this.treeData = this.groupUtilsService.groupsToGroupNodes(groups);
      if (!this.new) {
        this.caseService.getCaseTasksForCaseId(id, true).subscribe(
          resp => {
            this.case.tasks = (resp as TaskDTO[]).map(x => x.task);
            this.case.tasks.sort((a, b) => a.order - b.order);
            this.tasksPresent = this.case.tasks.length > 0 ? true : false;
            this.selectedItems = this.caseToSelectedIds(this.case);
            this.loading = false;
          },
          err => this.errorService.onError(err)
        );
      } else {
        this.loading = false;
      }
    });
  }

  getCaseById(id: string) {
    this.caseService.getCaseById(id).subscribe(
      resp => {
        this.case = resp as Case;
      },
      err => this.errorService.onError(err)
    );
  }

  back() {
    history.back();
    // this.router.navigateByUrl("/casussen");
  }

  caseToSelectedIds(_case: Case): CaseWorkGroupsGroupNode[] {
    let selectedIds: CaseWorkGroupsGroupNode[] = [];
    for (let group of _case.connectedGroups) {
      selectedIds.push({
        id: group.id,
        type: CaseTaskWorkgroupType.newStudents,
      });
    }
    for (let task of _case.tasks) {
      for (let reg of task.registrations) {
        if (!selectedIds.find(x => x.id == reg.workgroup.id)) {
          selectedIds.push({
            id: reg.workgroup.id,
            type: reg.workgroup.isStudent ? CaseTaskWorkgroupType.student : CaseTaskWorkgroupType.workgroup,
          });
        }
      }
    }
    return selectedIds;
  }

  updateConnectedGroups(_case: Case, selectedItem: CaseWorkGroupsGroupNode): Case {
    const existingConnectedGroup = this.case.connectedGroups.find(group => group.id == selectedItem.id);

    if (!existingConnectedGroup) {
      var g = new Group();
      g.id = selectedItem.id;
      if (!_case.connectedGroups.find(x => x.id == g.id)) {
        _case.connectedGroups.push(g);
      }
    } else {
      if (!_case.connectedGroups.find(x => x.id == existingConnectedGroup.id)) {
        _case.connectedGroups.push(existingConnectedGroup);
      }
    }
    return _case;
  }

  selectedIdsToCase(selectedItems: CaseWorkGroupsGroupNode[]): Case {
    let caseCopy = this.commonService.cloneObject<Case>(this.case);
    caseCopy.tasks = caseCopy.tasks.map(task => {
      return { ...task, registrations: [] };
    });
    caseCopy.connectedGroups = [];
    for (let task of this.case.tasks) {
      let newTask = this.commonService.cloneObject<Task>(task);
      newTask.registrations = [];
      for (let item of selectedItems) {
        if (item.type == 'workgroup' || item.type == 'student') {
          newTask = this.caseUtilsService.updateTaskRegistrations(this.allGroups, newTask, task, item.id);
          let toUpdateTask = caseCopy.tasks.find(x => x.id == newTask.id);
          if (toUpdateTask) {
            toUpdateTask.registrations = newTask.registrations;
          }
        }
        if (item.type == 'new-students') {
          caseCopy = this.updateConnectedGroups(caseCopy, item);
        }
      }
    }
    return caseCopy;
  }

  onSaveClick() {
    this.btnConfig.loading = true;
    this.case = this.selectedIdsToCase(this.selectedItems);
    this.caseService.editCase(this.case).subscribe({
      next: () => {
        this.notificationService.success(
          this.languageService.translateWithCap('general.notifications.changes-saved'),
          this.languageService.translateWithCap('case.workgroups.link-students.notification.message') + '.'
        );
        this.btnConfig.loading = false;
      },
      error: err => this.errorService.onError(err),
    });
  }
}
